import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState('All');
  const [loading, setLoading] = useState(true);

  const filters = ['All', 'Commercial', 'Industrial', 'Residential', 'Retail'];

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects`);
        if (response.ok) {
          const data = await response.json();
          setProjects(data);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
      setLoading(false);
    };
    fetchProjects();
  }, []);

  const filteredProjects = filter === 'All' 
    ? projects 
    : projects.filter(p => p.category === filter);

  return (
    <main className="pt-20" data-testid="projects-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#3D3A38]">
        <div className="container-custom">
          <p className="section-label mb-4 text-[#F5F1EC]/60">Projects</p>
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl text-[#F5F1EC] mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
          >
            Our work speaks for <span className="italic text-[#B8A066]">itself.</span>
          </h1>
          <p 
            className="text-lg text-[#F5F1EC]/60 max-w-2xl"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            A selection of commercial, residential, and industrial electrical projects delivered across the Greater Toronto Area.
          </p>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-24 md:py-32 bg-[#F5F1EC]">
        <div className="container-custom">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-16 justify-center">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`text-xs tracking-[0.15em] uppercase px-6 py-3 transition-all duration-300 ${
                  filter === f 
                    ? 'bg-[#3D3A38] text-[#F5F1EC]' 
                    : 'bg-transparent text-[#3D3A38] border border-[#3D3A38]/20 hover:border-[#3D3A38]'
                }`}
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid={`filter-${f.toLowerCase()}`}
              >
                {f}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-[#B8A066] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#5C5855]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                No projects found in this category.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <div 
                  key={project.id || index}
                  className="group"
                  data-testid={`project-card-${index}`}
                >
                  {/* Image */}
                  <div className="relative h-[280px] mb-6 overflow-hidden">
                    <img 
                      src={project.image || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80'}
                      alt={project.title}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4">
                      <span 
                        className="text-xs tracking-[0.1em] uppercase px-3 py-1 bg-[#3D3A38] text-[#F5F1EC]"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                      >
                        {project.category}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <h3 
                    className="text-xl text-[#3D3A38] mb-2"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {project.title}
                  </h3>
                  <p 
                    className="text-sm text-[#5C5855] mb-4 leading-relaxed line-clamp-2"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {project.description}
                  </p>
                  
                  {project.scope && (
                    <p 
                      className="text-xs text-[#B8A066] italic"
                      style={{ fontFamily: 'Cormorant Garamond, serif' }}
                    >
                      {project.scope}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#3D3A38]">
        <div className="container-custom text-center">
          <h2 
            className="text-3xl md:text-4xl text-[#F5F1EC] mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
          >
            Have a project in mind?
          </h2>
          <p 
            className="text-[#F5F1EC]/60 mb-8 max-w-xl mx-auto"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Let's discuss how we can bring your vision to life with quality electrical work.
          </p>
          <Link 
            to="/contact"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="projects-cta"
          >
            Start a Conversation <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}
